import type {
  AdminUser, CreatedUserCredentials, Role,
  DashboardData, FeedbackRecord, ManagedSource, ManagedSourceInput, MonitoringProfile, PreferenceSignal,
  CaseFeedbackInput, CaseFeedbackResult, CaseFeedbackStatus,
  ResearchCase, ScanFeedbackInput, ScanFeedbackRecord, ScanRun, ScanRunDetail, SearchConfigurationState,
  SearchPromptPreview, Sentiment, SessionResponse, UsageSummary
} from "../shared/contracts";
import type { KnowledgeDocument } from "../shared/chat";

let csrfToken = "";

export class ApiError extends Error {
  /** `body` traz campos de domínio além da mensagem (ex.: code e caseId do 409 de avaliação pendente). */
  constructor(message: string, public readonly status: number, public readonly body: Record<string, unknown> = {}) { super(message); }
}

export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData) && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (csrfToken && options.method && !["GET", "HEAD"].includes(options.method)) headers.set("x-taxos-csrf", csrfToken);
  const response = await fetch(path, { ...options, headers, credentials: "include" });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: "Falha na comunicação com o TAXOS." })) as { error?: string } & Record<string, unknown>;
    throw new ApiError(payload.error || "Falha na comunicação com o TAXOS.", response.status, payload);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  async login(email: string, password: string) {
    const session = await request<SessionResponse>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    csrfToken = session.csrfToken;
    return session;
  },
  async session() {
    const session = await request<SessionResponse>("/api/auth/session");
    csrfToken = session.csrfToken;
    return session;
  },
  async logout() {
    await request<void>("/api/auth/logout", { method: "POST" });
    csrfToken = "";
  },
  async changePassword(newPassword: string) {
    const session = await request<SessionResponse>("/api/auth/change-password", { method: "POST", body: JSON.stringify({ newPassword }) });
    csrfToken = session.csrfToken;
    return session;
  },
  users: (query = "") => request<AdminUser[]>(`/api/admin/users${query ? `?query=${encodeURIComponent(query)}` : ""}`),
  createUser: (input: { name: string; email: string; role: Role }) =>
    request<CreatedUserCredentials>("/api/admin/users", { method: "POST", body: JSON.stringify(input) }),
  updateUser: (id: string, changes: { name?: string; role?: Role; active?: boolean }) =>
    request<AdminUser>(`/api/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(changes) }),
  resetUserPassword: (id: string) => request<CreatedUserCredentials>(`/api/admin/users/${id}/reset-password`, { method: "POST" }),
  dashboard: () => request<DashboardData>("/api/dashboard"),
  scans: () => request<ScanRun[]>("/api/scans"),
  scan: (id: string) => request<ScanRunDetail>(`/api/scans/${id}`),
  runScan: (lookbackDays: number) => request<ScanRun>("/api/scans/run", { method: "POST", body: JSON.stringify({ lookbackDays }) }),
  saveScanFeedback: (id: string, input: ScanFeedbackInput) =>
    request<ScanFeedbackRecord>(`/api/scans/${id}/feedback`, { method: "POST", body: JSON.stringify(input) }),
  retryScan: (id: string) => request<ScanRun>(`/api/scans/${id}/retry`, { method: "POST" }),
  caseDocuments: (caseId: string) => request<KnowledgeDocument[]>(`/api/cases/${caseId}/documents`),
  attachCaseDocument: (caseId: string, file: File) => {
    const body = new FormData(); body.append("file", file);
    return request<KnowledgeDocument>(`/api/cases/${caseId}/documents`, { method: "POST", body });
  },
  attachCaseDocumentFromSharePoint: (caseId: string, item: { driveId: string; itemId: string }) =>
    request<KnowledgeDocument>(`/api/cases/${caseId}/documents/sharepoint`, { method: "POST", body: JSON.stringify(item) }),
  removeCaseDocument: (caseId: string, documentId: string) =>
    request<void>(`/api/cases/${caseId}/documents/${documentId}`, { method: "DELETE" }),
  cases: () => request<ResearchCase[]>("/api/cases"),
  getCase: (id: string) => request<ResearchCase>(`/api/cases/${id}`),
  createCase: (input: { title?: string; jurisdiction?: string; topic?: string }) =>
    request<ResearchCase>("/api/cases", { method: "POST", body: JSON.stringify(input) }),
  caseFeedbackStatus: () => request<CaseFeedbackStatus>("/api/case-feedback/status"),
  saveCaseFeedback: (id: string, input: CaseFeedbackInput) =>
    request<CaseFeedbackResult>(`/api/cases/${id}/feedback`, { method: "POST", body: JSON.stringify(input) }),
  skipCaseFeedback: (id: string) => request<CaseFeedbackResult>(`/api/cases/${id}/feedback/skip`, { method: "POST" }),
  sendMessage: (id: string, message: string) =>
    request<ResearchCase>(`/api/cases/${id}/messages`, { method: "POST", body: JSON.stringify({ message }) }),
  saveFeedback: (input: { targetType: "insight" | "message"; targetId: string; sentiment: Sentiment; comment?: string }) =>
    request<FeedbackRecord>("/api/feedback", { method: "POST", body: JSON.stringify(input) }),
  preferences: (history = false) => request<PreferenceSignal[]>(`/api/preferences${history ? "?history=true" : ""}`),
  removePreference: (id: string) => request<void>(`/api/preferences/${id}`, { method: "DELETE" }),
  resetPreferences: () => request<{ removed: number }>("/api/preferences/reset", { method: "POST" }),
  profile: () => request<MonitoringProfile>("/api/profile"),
  updateProfile: (profile: Omit<MonitoringProfile, "id" | "version">) =>
    request<MonitoringProfile>("/api/profile", { method: "PUT", body: JSON.stringify(profile) }),
  searchConfiguration: () => request<SearchConfigurationState>("/api/search-config"),
  saveSearchDraft: (profile: Omit<MonitoringProfile, "id" | "version">) =>
    request<SearchConfigurationState>("/api/search-config/draft", { method: "PUT", body: JSON.stringify(profile) }),
  previewSearchConfiguration: (profile: Omit<MonitoringProfile, "id" | "version">) =>
    request<SearchPromptPreview>("/api/search-config/preview", { method: "POST", body: JSON.stringify(profile) }),
  testSearchConfiguration: () => request<ScanRunDetail>("/api/search-config/test", { method: "POST" }),
  activateSearchConfiguration: () => request<SearchConfigurationState>("/api/search-config/activate", { method: "POST" }),
  rollbackSearchConfiguration: (version: number) => request<SearchConfigurationState>(`/api/search-config/rollback/${version}`, { method: "POST" }),
  sources: () => request<ManagedSource[]>("/api/sources"),
  createSource: (input: ManagedSourceInput) => request<ManagedSource>("/api/sources", { method: "POST", body: JSON.stringify(input) }),
  updateSource: (id: string, input: Partial<ManagedSourceInput>) =>
    request<ManagedSource>(`/api/sources/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  archiveSource: (id: string) => request<void>(`/api/sources/${id}`, { method: "DELETE" }),
  usage: () => request<UsageSummary>("/api/usage")
};
